import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { LobBankAccount } from '../../../../models/entities/lob-bank-account';
import { BillPayService } from '../../../../services/members/bill-pay.service';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { OrganizationService } from '../../../../services/organization/organization.service';
import moment from 'moment-timezone';
import { MembersService } from '../../../../services/members/members.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Organization } from '../../../../models/entities/organization';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { ValidationMessageService } from '../../../../services/validation-message.service';
import { Membership } from '../../../../models/entities/membership';
import { SharedModule } from '../../../shared/shared.module';
import type { PhSelectItem } from '@payhoa/ui-kit/elements';
import { ControlMessagesComponent } from '../../../shared/control-messages/control-messages.component';

export interface AddBillPayBankAccountInput
{
    memberId?: number;
}

export interface AddBillPayBankAccountOutput
{
    account: LobBankAccount;
    success: boolean;
    launchMicrodeposits?: boolean;
}

export interface OrganizationInput
{
    organization: Organization;
}

@UntilDestroy()
@Component({
    selector: 'app-add-bill-pay-account-dialog',
    templateUrl: './add-bill-pay-account.component.html',
    standalone: true,
    imports: [
        SharedModule,
        ControlMessagesComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AddBillPayAccountComponent implements OnInit
{
    @Input() open = false;
    @Input() size: 'md' | 'lg' = 'md';
    @Output() readonly closed = new EventEmitter<AddBillPayBankAccountOutput | null>();

    model: UntypedFormGroup = this._builder.group({
        description: [
            '',
            Validators.required,
        ],
        routingNumber: [
            '',
            Validators.required,
        ],
        accountNumber: [
            '',
            Validators.required,
        ],
        // SD-8201 / CORE-14F: Lob rejects a signatory over 30 characters. Without a local rule the
        // name passed validation, went out to Lob, and came back as a handled 400 the user saw as an
        // API error instead of an inline field message. `maxlength` already resolves to
        // "This field is too long." via ValidationMessageService, so lf-control-messages renders it.
        signatory: [
            '',
            [Validators.required, Validators.maxLength(30)],
        ],
        shareWithPmc: [
            false,
        ],
    });
    errorMessage = 'This field is required.';

    // ids
    organizationId: number = null;
    membership: Membership = null;

    // state
    isTermsAgreedTo = false;
    isMemberDataLoaded = false;
    isAgreeInFlight = false;
    isSubmitInFlight = false;
    isPreferencesLoaded = false;
    isManualAddEnabled = false;
    unverifiedAccounts: LobBankAccount[] = [];

    get unverifiedAccountItems(): PhSelectItem[] {
        return this.unverifiedAccounts.map(account => ({value: account.id, label: account.description}));
    }
    isPmc = false;

    jwt: JwtLegFiClaims;

    constructor(
            private _billPayService: BillPayService,
            private _builder: UntypedFormBuilder,
            private _membersService: MembersService,
            private _organizationService: OrganizationService,
            private _growler: GrowlerService,
            private _validationMessageService: ValidationMessageService,
    ) {
    }

    ngOnInit() {
        this.jwt = LegFiJwtService.read();

        this.getOrgPreferences();
        this.getMember();
    }

    isInvalid(name: string): boolean {
        const control = this.model.get(name);
        return control.invalid && control.touched;
    }

    onVerifyAccountSelected(accountId: number): void {
        const account = this.unverifiedAccounts.find(acct => acct.id === accountId);
        if (account) {
            this.launchVerification(account);
        }
    }

    launchVerification(account: LobBankAccount) {
        this.closed.emit(<AddBillPayBankAccountOutput>{
            success: true,
            launchMicrodeposits: true,
            account: account,
        });
    }

    getOrgPreferences() {
        this._organizationService
                .getOrganizationById(null, ['preferences'])
                .pipe(untilDestroyed(this))
                .subscribe(
                        (org: Organization) => {
                            this.isPmc = !!org.propertyManagementCompanyId;
                            this.isPreferencesLoaded = true;
                            this.isManualAddEnabled = org.preferences.billPayBankAccountAddingEnabled;
                        },
                );
    }

    getMember() {
        this._membersService
                .getMember(this.jwt.orgId, this.jwt.memberId, ['lobBankAccounts'])
                .pipe(untilDestroyed(this))
                .subscribe({
                    // Success
                    next: (member: Membership) => {
                        this.membership = member;
                        const isTermsAgreedToDate = member.profile.agreedToBillpayTerms;
                        const reasonableDate = moment.utc('2017-01-01');
                        this.isTermsAgreedTo = isTermsAgreedToDate && isTermsAgreedToDate.isAfter(reasonableDate);
                        this.isMemberDataLoaded = true;
                        this.unverifiedAccounts = this.membership.lobBankAccounts.filter(acct => !acct.verified);
                    },
                    error: () => {
                        if (this.jwt.superUser) {
                            this.isTermsAgreedTo = true;
                            this.isMemberDataLoaded = true;
                        }
                    },
                });
    }

    saveTermAgreement() {
        const requestBody: { [key: string]: any } = {};
        requestBody['siteId'] = 1;
        requestBody['agreedToBillpayTerms'] = true;
        this.isAgreeInFlight = true;

        this._membersService
                .updateMember(
                        this.jwt.memberId,
                        requestBody,
                )
                .pipe(untilDestroyed(this))
                .subscribe(
                        () => {
                            this.isTermsAgreedTo = true;
                            this.isAgreeInFlight = false;
                        },
                        () => {
                            this.isAgreeInFlight = false;
                            this._growler.error(
                                    'Oops!',
                                    'There was a problem updating your agreement details.',
                            );
                        },
                );
    }

    cancelTermAgreement() {
        this._growler.error(
                'Warning',
                'You must agree to the Terms & Conditions before adding a bill pay bank account.',
        );

        this.closed.emit(null);
    }

    saveBankInfo() {
        if (this.isSubmitInFlight) {
            return;
        }

        this.model.markAllAsTouched();
        if (!this.model.valid) {
            this._growler.error(
                    'Error',
                    'Please check all required fields',
            );
            return;
        }

        this.isSubmitInFlight = true;

        const requestBody = this.model.getRawValue();

        this._billPayService
                .createLobBankAccount(requestBody)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (bankAccount: LobBankAccount) => {
                        this._growler.success(
                                'Success',
                                'Your bank account has been added. Please return to enter microdeposits in 1-2 days.',
                        );

                        this.closed.emit(<AddBillPayBankAccountOutput>{
                            account: bankAccount,
                            success: true,
                        });
                    },
                    error: (err) => {
                        this.isSubmitInFlight = false;
                        this._growler.oops(this._validationMessageService.extractResponseErrorText(err));
                    },
                });
    }

}
